import {
	OrderCancellationReasonEnum,
	type OrderDetailResponseDto,
	OrderStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { FadeHeightAnimation } from "Shared/Components/Animation/In/FadeHeightAnimation";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { CLAIM_STATUS_ACTIVE } from "Shared/consts";
import { Grid2, Stack } from "@mui/material";
import dayjs from "dayjs";
import { AnimatePresence } from "motion/react";
import { memo, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";

/*
 * Order status can only be changed until the 8th day of the each month
 */
const LAST_STATUS_CHANGE_DAY = 8;

const allowedStatusTransitions: Partial<Record<OrderStatus, OrderStatus[]>> = {
	pending: [OrderStatus.pending, OrderStatus.confirmed, OrderStatus.cancelled],
	confirmed: [
		OrderStatus.confirmed,
		OrderStatus.in_progress,
		OrderStatus.cancelled,
	],
	in_progress: [
		OrderStatus.in_progress,
		OrderStatus.done,
		OrderStatus.confirmed,
		OrderStatus.cancelled,
	],
	done: [
		OrderStatus.done,
		OrderStatus.confirmed,
		OrderStatus.in_progress,
		OrderStatus.cancelled,
	],
	cancelled: [
		OrderStatus.cancelled,
		OrderStatus.pending,
		OrderStatus.confirmed,
	],
};

type StatusFromProps = {
	status?: OrderStatus | null;
	storedStatus: OrderStatus;
	isDisabled?: boolean;
	order: OrderDetailResponseDto;
};

const MemoizedFadeHeightAnimation = memo(FadeHeightAnimation);
const orderResources = Resources.base.order.editForm;
const selectOptionsResources = Resources.base.order.selectOptions;

export function StatusForm({
	status,
	storedStatus,
	order,
	isDisabled = false,
}: StatusFromProps) {
	const { t } = useResource();
	const currentDay = dayjs().date();
	const isActiveClaim = order.claims.some(
		(claim) => claim.status === CLAIM_STATUS_ACTIVE,
	);

	/**
	 * Sets the minimum date for the "dateDone" field.
	 *
	 * Example:
	 * - If today is the 10th of the month, minDate will be set to the 7th of the current month.
	 * - If today is the 5th of the month, minDate will be set to the 7th of the previous month.
	 * - If order.dateStarting is after the calculated minDate, it will be used instead.
	 * - The minimum date cannot be earlier than today to prevent selecting past dates.
	 */
	const minDate = useMemo(() => {
		let calculatedMinDate = dayjs();
		const startingDay = order?.dateStarting;
		const today = dayjs().startOf("day");

		if (startingDay && dayjs(startingDay).isAfter(dayjs())) {
			return dayjs(startingDay);
		}

		if (currentDay >= LAST_STATUS_CHANGE_DAY) {
			calculatedMinDate = dayjs().set("date", LAST_STATUS_CHANGE_DAY - 1);
		} else {
			calculatedMinDate = dayjs()
				.subtract(1, "month")
				.startOf("month")
				.set("date", LAST_STATUS_CHANGE_DAY);
		}

		if (startingDay) {
			calculatedMinDate = dayjs(startingDay).isAfter(calculatedMinDate)
				? dayjs(startingDay)
				: calculatedMinDate;
		}

		// Ensure the minimum date is not earlier than today to prevent selecting past dates
		return calculatedMinDate.isAfter(today) ? calculatedMinDate : today;
	}, [currentDay, order?.dateStarting]);

	const {
		control,
		formState: { errors },
	} = useFormContext();

	const statusOptions = useMemo(() => {
		const allOptions = createSelectOptions(
			OrderStatus,
			t,
			selectOptionsResources.orderStatus,
		);

		if (!storedStatus) {
			return allOptions;
		}

		const allowed = allowedStatusTransitions[storedStatus] ?? [];
		return allOptions.filter((option) =>
			allowed.includes(option.value as OrderStatus),
		);
	}, [storedStatus, t, selectOptionsResources.orderStatus]);

	const cancellationReasonOptions = useMemo(
		() =>
			createSelectOptions(
				OrderCancellationReasonEnum,
				t,
				selectOptionsResources.orderCancellationReason,
			),
		[t, selectOptionsResources.orderCancellationReason],
	);

	useEffect(() => {
		if (isActiveClaim && status === OrderStatus.done) {
			toast.warning(
				"You have an active claim on this order. Please resolve the claim before marking the order as done.",
			);
		}
	}, [isActiveClaim, status]);
	const isStatusChangeBlocked = isActiveClaim && status === OrderStatus.done;
	return (
		<Stack>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="status"
						name="status"
						variant="outlined"
						control={control}
						errors={errors}
						options={statusOptions}
						sxLabel={{ mt: 0 }}
						fullWidth
						disabled={isDisabled}
					/>
				</Grid2>
				<AnimatePresence>
					{status === OrderStatus.cancelled && (
						<MemoizedFadeHeightAnimation key={OrderStatus.cancelled}>
							<Grid2 size={12}>
								<Select
									id="cancellation-reason"
									name="cancellationReason"
									label={t(orderResources.cancellationReason)}
									variant="outlined"
									control={control}
									errors={errors}
									options={cancellationReasonOptions}
									fullWidth
								/>
							</Grid2>
						</MemoizedFadeHeightAnimation>
					)}
					{status === OrderStatus.confirmed && (
						<MemoizedFadeHeightAnimation key={OrderStatus.confirmed}>
							<Grid2 size={12}>
								<DatePicker
									name="dateStarting"
									control={control}
									errors={errors}
									label={t(orderResources.dateStarting)}
								/>
							</Grid2>
						</MemoizedFadeHeightAnimation>
					)}
					{status === OrderStatus.done && (
						<MemoizedFadeHeightAnimation key={OrderStatus.done}>
							<Grid2 size={12}>
								<DatePicker
									name="dateDone"
									control={control}
									errors={errors}
									label={t(orderResources.dateDone)}
									minDate={minDate}
									disabled={isStatusChangeBlocked}
								/>
							</Grid2>
						</MemoizedFadeHeightAnimation>
					)}
				</AnimatePresence>
			</Grid2>
		</Stack>
	);
}
